import fromUnixTime from 'date-fns/fromUnixTime';
import format from 'date-fns/format';
import * as Sentry from '@sentry/vue';

export const downloadCsvFile = (fileName, content) => {
  const contentType = 'data:text/csv;charset=utf-8;';
  const blob = new Blob([content], { type: contentType });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.setAttribute('download', fileName);
  link.setAttribute('href', url);
  link.click();
  return link;
};

export const generateFileName = ({ type, to, businessHours = false }) => {
  let name = `${type}-report-${format(fromUnixTime(to), 'dd-MM-yyyy')}`;
  if (businessHours) {
    name = `${name}-business-hours`;
  }
  return `${name}.csv`;
};

/**
 * Extracts filename from Content-Disposition header
 * @param {string} disposition - Content-Disposition header value
 * @param {string} defaultFilename - Fallback filename if extraction fails
 * @returns {string} Extracted or default filename
 */
export const extractFilenameFromHeader = (disposition, defaultFilename) => {
  if (!disposition || disposition.indexOf('filename=') === -1) {
    return defaultFilename;
  }
  const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  if (match && match[1]) {
    return match[1].replace(/['"]/g, '');
  }
  return defaultFilename;
};

/**
 * Downloads blob data as a file
 * @param {Blob|ArrayBuffer|string} data - Data to download
 * @param {string} filename - Name for the downloaded file
 * @param {string} contentType - MIME type of the content (optional)
 */
export const downloadBlob = (data, filename, contentType = null) => {
  const blob =
    data instanceof Blob ? data : new Blob([data], { type: contentType });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

/**
 * Downloads data from an API response with Content-Disposition header
 * @param {Object} response - Axios response object with data and headers
 * @param {string} defaultFilename - Fallback filename if header is missing
 */
export const downloadFromResponse = (response, defaultFilename) => {
  const filename = extractFilenameFromHeader(
    response.headers['content-disposition'],
    defaultFilename
  );
  downloadBlob(response.data, filename);
};

/**
 * Extracts filename from URL query parameters or path
 * @param {string} url - URL to extract filename from
 * @param {string} fallbackFilename - Fallback filename if extraction fails
 * @returns {string} Extracted or fallback filename
 */
export const extractFilenameFromUrl = (url, fallbackFilename = 'download') => {
  try {
    // Try to get filename from response-content-disposition parameter (S3 style)
    if (url.includes('response-content-disposition=')) {
      const dispositionMatch = url.match(
        /response-content-disposition=([^&]+)/
      );
      if (dispositionMatch) {
        const decoded = decodeURIComponent(dispositionMatch[1]);
        const filenameMatch = decoded.match(/filename[*]?=["']?([^"';]+)/);
        if (filenameMatch) {
          // Handle UTF-8'' encoding prefix
          let filename = filenameMatch[1];
          if (filename.startsWith("UTF-8''")) {
            filename = decodeURIComponent(filename.substring(7));
          }
          return filename;
        }
      }
    }

    // Try to get filename from inline; filename= parameter
    if (url.includes('filename=')) {
      const match = url.match(/filename=["']?([^"'&]+)/);
      if (match) {
        return decodeURIComponent(match[1]);
      }
    }

    // Fallback to last part of pathname
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const lastSegment = pathname.split('/').pop();
    if (lastSegment && lastSegment.includes('.')) {
      return decodeURIComponent(lastSegment);
    }
  } catch (error) {
    Sentry.captureException(error);
  }

  return fallbackFilename;
};

/**
 * Downloads file from URL using fetch with cache buster
 * @param {string} url - URL of the file to download
 * @param {string} filename - Optional filename (will be extracted from URL if not provided)
 */
export const downloadFromUrl = async (url, filename = null) => {
  const urlObj = new URL(url);
  urlObj.searchParams.set(
    '_cb', // Add cache buster parameter to avoid cache poisoning
    Date.now() + Math.random().toString(36).substring(2, 9)
  );

  const response = await fetch(urlObj.toString());
  const blob = await response.blob();

  downloadBlob(blob, filename || extractFilenameFromUrl(url));
};
